import apiClient from './apiClient';
import i18n from '../i18n/config';

class EnvironmentService {
  constructor() {
    this.api = apiClient;
  }

  // Environment CRUD operations
  async getEnvironments() {
    const response = await this.api.get('/environments/');
    return response.data.data || [];
  }

  async getEnvironment(environmentId) {
    const response = await this.api.get(`/environments/${environmentId}`);
    return response.data;
  }

  async createEnvironment(environmentData) {
    const response = await this.api.post('/environments/', environmentData);
    return response.data;
  }

  async updateEnvironment(environmentId, environmentData) {
    const response = await this.api.put(`/environments/${environmentId}`, environmentData);
    return response.data;
  }

  async deleteEnvironment(environmentId) {
    const response = await this.api.delete(`/environments/${environmentId}`);
    return response.data;
  }

  // Environment activation/deactivation
  async activateEnvironment(environmentId) {
    const response = await this.api.post(`/environments/${environmentId}/activate`);
    return response.data;
  }

  async deactivateEnvironment(environmentId) {
    const response = await this.api.post(`/environments/${environmentId}/deactivate`);
    return response.data;
  }

  // License management
  async getEnvironmentLicenses(environmentId) {
    const response = await this.api.get(`/environments/${environmentId}/licenses`);
    return response.data;
  }

  async createLicense(environmentId, licenseData) {
    const response = await this.api.post(`/environments/${environmentId}/licenses`, licenseData);
    return response.data;
  }

  async updateLicense(environmentId, licenseId, licenseData) {
    const response = await this.api.put(`/environments/${environmentId}/licenses/${licenseId}`, licenseData);
    return response.data;
  }

  async deleteLicense(environmentId, licenseId) {
    const response = await this.api.delete(`/environments/${environmentId}/licenses/${licenseId}`);
    return response.data;
  }

  async assignLicenseToExecutor(environmentId, licenseId, executorId) {
    const response = await this.api.post(`/environments/${environmentId}/licenses/${licenseId}/assign`, {
      executor_id: executorId
    });
    return response.data;
  }

  async unassignLicenseFromExecutor(environmentId, licenseId) {
    const response = await this.api.post(`/environments/${environmentId}/licenses/${licenseId}/unassign`);
    return response.data;
  }

  // Environment usage and statistics
  async getEnvironmentUsage(environmentId) {
    const response = await this.api.get(`/environments/${environmentId}/usage`);
    return response.data;
  }

  async getEnvironmentStats() {
    const response = await this.api.get('/environments/stats');
    return response.data;
  }

  // Utility methods
  formatResourceSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatLicenseType(type) {
    return i18n.t(`environments.licenseTypes.${type}`, type);
  }

  formatEnvironmentType(type) {
    return i18n.t(`environments.types.${type}`, type);
  }

  getEnvironmentTypeColor(type) {
    const colors = {
      development: '#4CAF50',
      staging: '#FF9800',
      production: '#F44336',
      testing: '#2196F3'
    };
    return colors[type] || '#757575';
  }

  getLicenseStatusColor(status) {
    const colors = {
      active: '#4CAF50',
      expired: '#F44336',
      assigned: '#2196F3',
      unassigned: '#757575'
    };
    return colors[status] || '#757575';
  }
}

const environmentService = new EnvironmentService();
export default environmentService;
